import React, { useContext, useState } from "react";
import { motion } from "framer-motion";
import { Edit2, Check, X, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import DataContext from "../Context/DAtaContext";
import supabase from "../Config/Supabase";

const Objective = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentAmount, setCurrentAmount] = useState(null); // State to hold the current amount
  const [selectedObjectiveId, setSelectedObjectiveId] = useState(null); // State to hold the selected objective's ID
  const { Objective, fetchObjectives } = useContext(DataContext);
  const [loading, setLoading] = useState(false);

  const handleEdit = (objct) => {
    setCurrentAmount(objct?.amount); // Set current amount for editing
    setSelectedObjectiveId(objct?.id); // Set the selected objective ID
    setIsEditing(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("objective")
        .update({ amount: currentAmount }) // Update the amount field
        .eq("id", selectedObjectiveId) // Use the selected objective's ID
        .select();

      if (error) throw error; // Handle any errors
      toast.success("Update successful:");

      // Refresh objectives after successful update
      fetchObjectives();
      setIsEditing(false);
      setLoading(false);
      setSelectedObjectiveId(null); // Reset selected ID after save
    } catch (error) {
      toast.error("Error updating objective:", error);
      setLoading(false);
      console.error("Error updating objective:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedObjectiveId(null); // Reset selected ID on cancel
  };

  return (
    <div className="flex flex-col min-h-screen">
      {Objective?.map((objct) => {
        return (
          <div key={objct?.id} className="">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="max-w-sm p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
            >
              <div className="flex justify-between mb-2">
                <div>
                  <h1 className="text-lg font-medium text-gray-900 capitalize">
                    {objct?.metric}
                  </h1>
                  <h3 className="text-lg font-medium text-gray-900">Amount</h3>
                </div>
                {!isEditing && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleEdit(objct)} // Call handleEdit with the current objective
                    className="p-1 text-blue-500 transition-colors rounded-full hover:text-blue-600 hover:bg-blue-50"
                  >
                    <Edit2 size={16} />
                  </motion.button>
                )}
              </div>

              {isEditing && selectedObjectiveId === objct?.id ? ( // Check if editing the current objective
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-2"
                >
                  <input
                    type="number"
                    value={currentAmount || ""} // Bind input to currentAmount
                    onChange={(e) => setCurrentAmount(e.target.value)} // Handle input change
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    {loading ? (
                      <div className="flex items-center justify-center flex-1 bg-green-600 cursor-not-allowed">
                        <RefreshCw className="text-white animate-spin" />
                      </div>
                    ) : (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleSave}
                        className="flex items-center justify-center flex-1 px-4 py-1.5 space-x-1 text-white transition-colors bg-green-600 rounded-md hover:bg-green-500"
                      >
                        <Check size={16} />
                        <span>Save</span>
                      </motion.button>
                    )}

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleCancel}
                      className="flex items-center justify-center flex-1 px-4 py-1.5 space-x-1 text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      <X size={16} />
                      <span>Cancel</span>
                    </motion.button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-2xl font-semibold text-gray-700"
                >
                  {objct?.amount?.toLocaleString()}
                </motion.div>
              )}
            </motion.div>
          </div>
        );
      })}
    </div>
  );
};

export default Objective;
